import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Loader2, Bot } from 'lucide-react';

import AcceleratorCard from '../../components/common/AcceleratorCard';
import AcceleratorModal from '../../components/common/AcceleratorModal';
import SEO from '../../components/common/SEO';
import Pagination from '../../Pagination';
import { buildBrowseCanonicalUrl } from '../../utils/seoUtils';
import {
  getCategorySlug,
  getSubcategorySlug,
  findCategoryBySlug,
  findSubcategoryBySlug,
  resolveCategorySlugFromParams,
  resolveSubcategorySlugFromParams,
  buildBrowseSearchParams,
  browseSearchesEqual,
} from '../../utils/slugify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const BrowsePage = () => {
  const [accelerators, setAccelerators] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [techStacks, setTechStacks] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedTechStack, setSelectedTechStack] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccelerator, setSelectedAccelerator] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);

  const location = useLocation();
  const navigate = useNavigate();

  const updateBrowseUrl = useCallback((category, subcategory, techStack, search) => {
    const nextParams = buildBrowseSearchParams({
      category,
      subcategory,
      persona: techStack,
      search,
    });
    const nextSearch = nextParams.toString();
    const currentSearch = location.search.replace(/^\?/, '');

    if (!browseSearchesEqual(nextSearch, currentSearch)) {
      navigate(
        { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  const applyFilters = useCallback((updates) => {
    const nextCategory = updates.category !== undefined ? updates.category : selectedCategory;
    const nextSubcategory = updates.subcategory !== undefined ? updates.subcategory : selectedSubcategory;
    const nextTechStack = updates.techStack !== undefined ? updates.techStack : selectedTechStack;
    const nextSearch = updates.search !== undefined ? updates.search : searchQuery;

    setSelectedCategory(nextCategory);
    setSelectedSubcategory(nextSubcategory);
    setSelectedTechStack(nextTechStack);
    setSearchQuery(nextSearch);
    setCurrentPage(1);
    updateBrowseUrl(nextCategory, nextSubcategory, nextTechStack, nextSearch);
  }, [selectedCategory, selectedSubcategory, selectedTechStack, searchQuery, updateBrowseUrl]);

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
    fetchTechStacks();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const techStack = params.get('persona') || '';
    const search = params.get('search') || '';

    let categorySlug = '';
    let subcategorySlug = '';

    if (categories.length) {
      categorySlug = resolveCategorySlugFromParams(params, categories);
      if (subcategories.length) {
        subcategorySlug = resolveSubcategorySlugFromParams(
          params,
          subcategories,
          categories,
          categorySlug
        );
      }
    } else {
      categorySlug = params.get('category') || '';
      subcategorySlug = params.get('subcategory') || '';
    }

    setSelectedTechStack(techStack);
    setSearchQuery(search);
    setSelectedCategory(categorySlug);
    setSelectedSubcategory(subcategorySlug);
    setCurrentPage(1);
  }, [location.search, categories, subcategories]);

  useEffect(() => {
    if (!categories.length) return;

    const params = new URLSearchParams(location.search);
    const hasLegacyParams = params.has('category_id') || params.has('subcategory_id');
    const categorySlug = resolveCategorySlugFromParams(params, categories);
    const subcategorySlug = subcategories.length
      ? resolveSubcategorySlugFromParams(params, subcategories, categories, categorySlug)
      : '';

    const rawCategory = params.get('category') || '';
    const rawSubcategory = params.get('subcategory') || '';
    const needsCanonicalSlug =
      hasLegacyParams ||
      (rawCategory && rawCategory !== categorySlug) ||
      (rawSubcategory && rawSubcategory !== subcategorySlug);

    if (!needsCanonicalSlug) return;

    updateBrowseUrl(
      categorySlug,
      subcategorySlug,
      params.get('persona') || '',
      params.get('search') || ''
    );
  }, [categories, subcategories, location.search, updateBrowseUrl]);

  useEffect(() => {
    fetchAccelerators();
  }, [selectedCategory, selectedSubcategory, selectedTechStack, searchQuery, categories, subcategories]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/subcategories`);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchTechStacks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/techstack`);
      setTechStacks(response.data);
    } catch (error) {
      console.error('Error fetching tech stacks:', error);
      setTechStacks([
        { name: 'react', display_name: 'React' },
        { name: 'vue', display_name: 'Vue.js' },
        { name: 'angular', display_name: 'Angular' },
        { name: 'nodejs', display_name: 'Node.js' },
        { name: 'python', display_name: 'Python' },
        { name: 'java', display_name: 'Java' },
      ]);
    }
  };

  const fetchAccelerators = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();

      if (selectedCategory) {
        const categoryRecord = findCategoryBySlug(categories, selectedCategory);
        if (categories.length && !categoryRecord) {
          setAccelerators([]);
          return;
        }
        params.append('category', categoryRecord ? getCategorySlug(categoryRecord) : selectedCategory);
      }

      if (selectedSubcategory) {
        const subcategoryRecord = findSubcategoryBySlug(
          subcategories,
          selectedSubcategory,
          selectedCategory,
          categories
        );
        if (subcategories.length && !subcategoryRecord) {
          setAccelerators([]);
          return;
        }
        params.append(
          'subcategory',
          subcategoryRecord ? getSubcategorySlug(subcategoryRecord) : selectedSubcategory
        );
      }

      if (selectedTechStack) params.append('persona', selectedTechStack);
      if (searchQuery) params.append('search', searchQuery);

      const query = params.toString();
      const response = await axios.get(
        `${API_BASE_URL}/api/accelerators${query ? `?${query}` : ''}`
      );
      setAccelerators(response.data);
    } catch (error) {
      console.error('Error fetching accelerators:', error);
      setAccelerators([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceleratorClick = (accelerator) => {
    setSelectedAccelerator(accelerator);
    setIsModalOpen(true);
  };

  const selectedCategoryRecord = findCategoryBySlug(categories, selectedCategory);

  const filteredSubcategories = selectedCategoryRecord
    ? subcategories.filter((sub) => sub.category_id === selectedCategoryRecord.id)
    : [];

  const selectedSubcategoryRecord = findSubcategoryBySlug(
    subcategories,
    selectedSubcategory,
    selectedCategory,
    categories
  );

  const clearAllFilters = () => {
    applyFilters({
      category: '',
      subcategory: '',
      techStack: '',
      search: '',
    });
  };

  const hasActiveFilters = selectedCategory || selectedSubcategory || selectedTechStack || searchQuery;

  const selectedTechStackRecord = techStacks.find((p) => p.name === selectedTechStack);

  const browseSeo = useMemo(() => {
    const canonicalUrl = buildBrowseCanonicalUrl(location, {
      category: selectedCategory,
      subcategory: selectedSubcategory,
      persona: selectedTechStack,
      search: searchQuery,
    });

    if (selectedSubcategoryRecord) {
      const categoryName = selectedCategoryRecord?.name || '';
      return {
        title: `${selectedSubcategoryRecord.name} Accelerators`,
        description: `Browse ${selectedSubcategoryRecord.name} accelerators${categoryName ? ` in ${categoryName}` : ''}. Discover ready-to-deploy enterprise solutions tailored to your needs.`,
        h1: selectedSubcategoryRecord.name,
        keywords: `${selectedSubcategoryRecord.name}, ${categoryName}, enterprise accelerators`,
        canonicalUrl,
      };
    }

    if (selectedCategoryRecord) {
      return {
        title: `${selectedCategoryRecord.name} Accelerators`,
        description: `Explore ${selectedCategoryRecord.name} accelerators. Find ready-to-deploy enterprise solutions for rapid development and deployment.`,
        h1: selectedCategoryRecord.name,
        keywords: `${selectedCategoryRecord.name}, enterprise accelerators, AI agents`,
        canonicalUrl,
      };
    }

    if (searchQuery) {
      return {
        title: `Search Results for "${searchQuery}"`,
        description: `Search results for "${searchQuery}" in the KL Accelerators catalog. Find ready-to-deploy enterprise solutions matching your criteria.`,
        h1: 'Browse Accelerators',
        keywords: `${searchQuery}, accelerator search, enterprise solutions`,
        canonicalUrl,
      };
    }

    if (selectedTechStack) {
      const techName = selectedTechStackRecord?.display_name || selectedTechStack;
      return {
        title: `${techName} Accelerators`,
        description: `Browse accelerators built with ${techName}. Explore ready-to-deploy solutions for rapid enterprise development.`,
        h1: 'Browse Accelerators',
        keywords: `${techName}, tech stack accelerators, enterprise development`,
        canonicalUrl,
      };
    }

    return {
      title: 'Browse Accelerators',
      description: 'Explore our comprehensive catalog of accelerators for rapid development and deployment. Filter by category, subcategory, and tech stack.',
      h1: 'Browse Accelerators',
      keywords: 'browse accelerators, enterprise accelerators, AI agents, rapid deployment',
      canonicalUrl,
    };
  }, [
    location,
    selectedCategory,
    selectedSubcategory,
    selectedTechStack,
    searchQuery,
    selectedCategoryRecord,
    selectedSubcategoryRecord,
    selectedTechStackRecord,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={browseSeo.title}
        description={browseSeo.description}
        canonicalUrl={browseSeo.canonicalUrl}
        keywords={browseSeo.keywords}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{browseSeo.h1}</h1>
          <p className="text-lg text-gray-600">
            Explore our comprehensive catalog of accelerators for rapid development and deployment.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Filter Accelerators</h3>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                Clear All Filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search accelerators..."
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    searchQuery ? 'border-primary-300 bg-primary-50' : 'border-gray-300'
                  }`}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  onBlur={() => updateBrowseUrl(selectedCategory, selectedSubcategory, selectedTechStack, searchQuery)}
                />
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  selectedCategory ? 'border-primary-300 bg-primary-50' : 'border-gray-300'
                }`}
                value={selectedCategory}
                onChange={(e) => applyFilters({ category: e.target.value, subcategory: '' })}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={getCategorySlug(category)}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subcategory</label>
              <select
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  selectedSubcategory ? 'border-primary-300 bg-primary-50' : 'border-gray-300'
                } ${!selectedCategory ? 'opacity-60 cursor-not-allowed' : ''}`}
                value={selectedSubcategory}
                onChange={(e) => applyFilters({ subcategory: e.target.value })}
                disabled={!selectedCategory}
              >
                <option value="">All Subcategories</option>
                {filteredSubcategories.map((subcategory) => (
                  <option key={subcategory.id} value={getSubcategorySlug(subcategory)}>
                    {subcategory.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tech Stack</label>
              <select
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  selectedTechStack ? 'border-primary-300 bg-primary-50' : 'border-gray-300'
                }`}
                value={selectedTechStack}
                onChange={(e) => applyFilters({ techStack: e.target.value })}
              >
                <option value="">All Tech Stacks</option>
                {techStacks.map((techStack) => (
                  <option key={techStack.name || techStack} value={techStack.name || techStack}>
                    {techStack.display_name || techStack.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm text-gray-600">Active filters:</span>
                {selectedCategory && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Category: {selectedCategoryRecord?.name || selectedCategory}
                    <button
                      onClick={() => applyFilters({ category: '', subcategory: '' })}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedSubcategory && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    Subcategory: {selectedSubcategoryRecord?.name || selectedSubcategory}
                    <button
                      onClick={() => applyFilters({ subcategory: '' })}
                      className="ml-2 text-indigo-600 hover:text-indigo-800"
                    >
                      ×
                    </button>
                  </span>
                )}
                {selectedTechStack && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Tech Stack: {techStacks.find((p) => p.name === selectedTechStack)?.display_name || selectedTechStack.replace('_', ' ')}
                    <button
                      onClick={() => applyFilters({ techStack: '' })}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      ×
                    </button>
                  </span>
                )}
                {searchQuery && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Search: "{searchQuery}"
                    <button
                      onClick={() => applyFilters({ search: '' })}
                      className="ml-2 text-purple-600 hover:text-purple-800"
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <Loader2 className="h-12 w-12 text-primary-600 animate-spin mx-auto mb-4" />
              <p className="text-lg text-gray-600 mb-2">Loading Accelerators...</p>
              <p className="text-sm text-gray-500">Please wait while we fetch the latest accelerators for you</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {accelerators.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((accelerator) => (
                <AcceleratorCard
                  key={accelerator.id}
                  accelerator={accelerator}
                  onClick={handleAcceleratorClick}
                />
              ))}
            </div>

            <Pagination
              currentPage={currentPage}
              totalItems={accelerators.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
            {accelerators.length === 0 && (
              <div className="text-center py-12">
                <Bot className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No accelerators found</h3>
                <p className="text-gray-500">Try adjusting your search criteria.</p>
              </div>
            )}
          </>
        )}
      </div>

      <AcceleratorModal
        accelerator={selectedAccelerator}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};

export default BrowsePage;
